import { useAtom } from "jotai";
import { useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import { Button, SubmitButton } from "@/components/Interface/Button/Button";
import { CardModule } from "@/components/Layout/BaseLayouts/CardModule/CardModule";
import { useAlert } from "@/hooks/useAlert";
import { useRequestTwoFactorAppSetup } from "@/hooks/useRequestTwoFactorAppSetup";
import { useValidateTwoFactorAppCode } from "@/hooks/useValidateTwoFactorAppCode";
import {
  useSetUpTwoFactorAuthentication,
  useValidateTwoFactorAuthentication,
} from "@/services/auth";
import { ValidateTwoFactorAuthenticationMethod } from "@/services/model";
import { AlertTypes, twoFactorBackupCodesAtom } from "@/utils/atoms";

import { AuthenticatorAppForm } from "./AuthenticatorAppForm";
import { TwoFactorConfirmationCodeForm } from "./TwoFactorConfirmationInput";

export const AuthenticatorAppSetup = ({
  logoutOnAuthError,
  navigationTarget,
}: {
  logoutOnAuthError: boolean;
  navigationTarget: string;
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const showToast = useAlert();

  const { authSetupData, hasFailed } = useRequestTwoFactorAppSetup(
    useSetUpTwoFactorAuthentication,
    {
      method: ValidateTwoFactorAuthenticationMethod.APP,
    },
    true,
  );

  useEffect(() => {
    if (hasFailed) {
      showToast({
        text: t("translation:Common.generalForm.error.genericError"),
        type: AlertTypes.error,
      });
      navigate(-1);
    }
  }, [hasFailed, navigate, showToast, t]);

  const [, setBackupCodes] = useAtom(twoFactorBackupCodesAtom);
  // Form handling
  const formMethods = useForm<TwoFactorConfirmationCodeForm>({
    defaultValues: {
      code: "",
    },
    mode: "onChange",
  });

  const { isLoading, validateTwoFactorAuth } = useValidateTwoFactorAppCode(
    () => {
      formMethods.setError("code", {
        type: "manual",
        message: t("portal:authenticator.app.setup.step2.invalidCode"),
      });
    },
    logoutOnAuthError,
    useValidateTwoFactorAuthentication,
  );

  const onSubmit = (data: TwoFactorConfirmationCodeForm) => {
    validateTwoFactorAuth({
      params: {
        code: parseInt(data.code),
        method: ValidateTwoFactorAuthenticationMethod.APP,
      },
    }).then((data) => {
      if (data.backupCodes) {
        setBackupCodes(data.backupCodes);
        navigate(navigationTarget);
      }
    });
  };

  return (
    <FormProvider {...formMethods}>
      <form onSubmit={formMethods.handleSubmit(onSubmit)}>
        <CardModule
          stickyHeader
          title={t("portal:user-account.security.2fa.initialSetup.title")}
          actions={
            <>
              <Button
                variant="secondary"
                size="sm"
                type="button"
                onClick={() => navigate(-1)}
              >
                {t("Common.label.cancel")}
              </Button>
              <SubmitButton
                fullWidth={false}
                size="sm"
                disabled={isLoading}
                label={t("Common.label.finish")}
              />
            </>
          }
        >
          <AuthenticatorAppForm authSetupData={authSetupData} />
        </CardModule>
      </form>
    </FormProvider>
  );
};
